import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowDown,
  lucideArrowUp,
  lucideArrowUpDown,
  lucideCalendarX,
  lucideCheck,
  lucideChevronsUpDown,
  lucideCircleCheck,
  lucideRotateCcw,
  lucideTicket,
  lucideTicketCheck,
} from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmEmptyImports } from '@spartan-ng/helm/empty';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import type { Voucher, SortField, SortOrder } from '../../services/voucher.service';
import { VoucherService } from '../../services/voucher.service';
import { VoucherDetailsComponent } from '../voucher-details/voucher-details.component';
import { TablePaginationComponent } from '../table-pagination/table-pagination.component';
import { formatCurrency } from '../../utils/currency.utils';

@Component({
  selector: 'app-voucher-history',
  standalone: true,
  imports: [
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmDialogImports,
    HlmDropdownMenuImports,
    HlmEmptyImports,
    HlmItemImports,
    HlmSkeletonImports,
    HlmTableImports,
    HlmTooltipImports,
    TablePaginationComponent,
    VoucherDetailsComponent,
  ],
  providers: [
    provideIcons({
      lucideArrowDown,
      lucideArrowUp,
      lucideArrowUpDown,
      lucideCalendarX,
      lucideCheck,
      lucideChevronsUpDown,
      lucideCircleCheck,
      lucideRotateCcw,
      lucideTicket,
      lucideTicketCheck,
    }),
  ],
  templateUrl: './voucher-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './voucher-history.component.css',
})
export class VoucherHistoryComponent {
  @Input() vouchers: Voucher[] | undefined = undefined;
  /** Renders skeleton rows instead of the empty state while the page loads. */
  @Input() loading = false;
  @Input() currentPage = 1;
  @Input() totalPages = 1;
  @Input() totalItems = 0;
  @Input() currentSortField?: SortField;
  @Input() currentSortOrder?: SortOrder;
  @Output() onPageChange = new EventEmitter<number>();
  @Output() onSort = new EventEmitter<{ field: SortField; order: SortOrder }>();
  showModal = false;
  selectedVoucherId: string | undefined = undefined;

  /** Row placeholders for the loading state. */
  skeletonRows = [1, 2, 3, 4, 5];

  sortFieldOptions = [
    { label: 'Datum', value: 'created_at' },
    { label: 'Betrag', value: 'amount' },
    { label: 'Code', value: 'code' },
  ];

  sortOrderOptions = [
    { label: 'Absteigend', value: 'desc' },
    { label: 'Aufsteigend', value: 'asc' },
  ];

  constructor(private voucherService: VoucherService) {}

  formatAmount(amount: number | undefined): string {
    return formatCurrency(amount);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  viewVoucherDetails(code: string) {
    // A member sees the network's vouchers without their codes: nothing to open.
    if (!code) return;
    this.selectedVoucherId = code;
    this.showModal = true;
  }

  onCloseModal() {
    this.showModal = false;
    this.selectedVoucherId = undefined;
  }

  handlePageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.onPageChange.emit(page);
    }
  }

  /** Dates and amounts read best newest/highest first; text columns A→Z. */
  private defaultOrderFor(field: SortField): SortOrder {
    return field === 'created_at' || field === 'amount' || field === 'initial_amount'
      ? 'desc'
      : 'asc';
  }

  handleSort(field: SortField) {
    const order: SortOrder =
      field === this.currentSortField
        ? this.currentSortOrder === 'asc'
          ? 'desc'
          : 'asc'
        : this.defaultOrderFor(field);
    this.onSort.emit({ field, order });
  }

  handleMobileSortField(value: string) {
    const field = value as SortField;
    const order =
      field === this.currentSortField
        ? (this.currentSortOrder ?? this.defaultOrderFor(field))
        : this.defaultOrderFor(field);
    this.onSort.emit({ field, order });
  }

  handleMobileSortOrder(value: string) {
    this.onSort.emit({ field: this.currentSortField || 'created_at', order: value as SortOrder });
  }

  /** Header icon: neutral for inactive columns, direction arrow for the sorted one. */
  sortIcon(field: SortField): string {
    if (field !== this.currentSortField) return 'lucideChevronsUpDown';
    return this.currentSortOrder === 'asc' ? 'lucideArrowUp' : 'lucideArrowDown';
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'redeemed':
        return 'Eingelöst';
      case 'expired':
        return 'Abgelaufen';
      case 'refunded':
        return 'Erstattet';
      default:
        return 'Aktiv';
    }
  }

  statusIcon(status: string): string {
    switch (status) {
      case 'redeemed':
        return 'lucideTicketCheck';
      case 'expired':
        return 'lucideCalendarX';
      case 'refunded':
        return 'lucideRotateCcw';
      default:
        return 'lucideCircleCheck';
    }
  }

  onDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed') {
      this.onCloseModal();
    }
  }

  isProductVoucher(voucher: Voucher): boolean {
    return this.voucherService.isProductVoucher(voucher);
  }

  isMoneyVoucher(voucher: Voucher): boolean {
    return this.voucherService.isMoneyVoucher(voucher);
  }

  /** Returns distinct member company names that redeemed this voucher. */
  getRedeemingMemberNames(voucher: Voucher): string[] {
    if (!voucher.redemptions || voucher.redemptions.length === 0) return [];
    const names = voucher.redemptions
      .map((r) => r.redeeming_company_name)
      .filter((n): n is string => !!n);
    return [...new Set(names)];
  }

  /** True when any voucher in the list has a redeeming member company name. */
  get hasRedeemingMemberColumn(): boolean {
    return (this.vouchers ?? []).some((v) => this.getRedeemingMemberNames(v).length > 0);
  }
}
